import type { ReactNode } from 'react';
import { createContext, useContext, useMemo } from 'react';
import { ValueType } from '@/widgets/cohortScanner';
import { useDataFetcher } from '@/shared/hooks';
import { useAppSelector } from '@/shared/model';
import { selectConfig } from '../../model/slice';
interface ContextState {
  min?: number;
  max?: number;
}

const Context = createContext<ContextState | null>(null);

interface Props {
  children: ReactNode;
  getMinMax: () => Promise<{ min: number; max: number }>;
}

export function HighlightMinMaxProvider({ children, getMinMax }: Props) {
  const { data: minMax } = useDataFetcher(getMinMax);

  const { highlight } = useAppSelector(selectConfig)!;
  const { min, max } = useMemo<{ min?: number; max?: number }>(() => {
    if (highlight.valueType === ValueType.percent) {
      return {
        min: 0,
        max: 1,
      };
    }

    return minMax ?? {};
  }, [highlight, minMax]);

  return <Context.Provider value={{ min, max }}>{children}</Context.Provider>;
}

export const useHighlightMinMaxContext = () => {
  const context = useContext(Context);

  if (!context) {
    throw new Error(
      'useHighlightMinMaxContext must be used within a HighlightMinMaxProvider',
    );
  }

  return context;
};
